import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Character, PlayerAppearance } from '../types/character';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import PlayerAppearanceEditor from './PlayerAppearanceEditor';
import { useConfirm } from './ConfirmModal';
import './CharacterList.css';

interface CharacterListProps {
  onCreateCharacter: () => void;
  onViewProgression?: (character: Character) => void;
  onManageEquipment?: (character: Character) => void;
}

// XP calculation functions (matching backend formula: 100 * level^1.5)
const getExperienceRequiredForLevel = (level: number): number => {
  if (level <= 1) return 0;
  return Math.floor(100 * Math.pow(level, 1.5));
};

const getTotalExperienceForLevel = (level: number): number => {
  let totalXP = 0;
  for (let i = 2; i <= level; i++) {
    totalXP += getExperienceRequiredForLevel(i);
  }
  return totalXP;
};

const CharacterList: React.FC<CharacterListProps> = ({
  onCreateCharacter,
  onViewProgression,
  onManageEquipment,
}) => {
  const { token, handleAuthError } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [appearanceCharacter, setAppearanceCharacter] = useState<Character | null>(null);
  const [appearanceDraft, setAppearanceDraft] = useState<PlayerAppearance | null>(null);
  const [savingAppearance, setSavingAppearance] = useState(false);

  const fetchCharacters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest('/characters');

      if (!response.ok) {
        throw new Error('Failed to fetch characters');
      }

      const data = await response.json();
      setCharacters(data);
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    fetchCharacters();
  }, [token, fetchCharacters]);

  const handleDeleteCharacter = async (id: number, name: string) => {
    const confirmed = await confirm({
      title: 'Delete Character',
      message: `Are you sure you want to delete ${name}?`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!confirmed) {
      return;
    }

    try {
      const response = await makeAuthenticatedRequest(`/characters/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete character');
      }

      setCharacters(prev => prev.filter(char => char.id !== id));
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    }
  };

  const openAppearanceEditor = (character: Character) => {
    setAppearanceCharacter(character);
    setAppearanceDraft({
      gender: character.gender,
      skinTone: character.skinTone,
      hairSprite: character.hairSprite ?? null,
      beardSprite: character.beardSprite ?? null,
    });
  };

  const closeAppearanceEditor = () => {
    setAppearanceCharacter(null);
    setAppearanceDraft(null);
  };

  const handleSaveAppearance = async () => {
    if (!appearanceCharacter || !appearanceDraft) return;

    try {
      setSavingAppearance(true);
      const response = await makeAuthenticatedRequest(`/characters/${appearanceCharacter.id}/appearance`, {
        method: 'POST',
        body: JSON.stringify(appearanceDraft)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update appearance');
      }

      setCharacters(prev => prev.map(c =>
        c.id === appearanceCharacter.id ? { ...c, ...appearanceDraft } : c
      ));
      closeAppearanceEditor();
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError(apiError.message);
    } finally {
      setSavingAppearance(false);
    }
  };

  if (loading) {
    return <div className="character-list-loading">Loading characters...</div>;
  }

  if (error) {
    return <div className="character-list-error">Error: {error}</div>;
  }

  return (
    <div className="character-list">
      {confirmDialog}
      <div className="character-list-header">
        <h2>Your Characters ({characters.length}/10)</h2>
        <button 
          onClick={onCreateCharacter}
          disabled={characters.length >= 10}
          className="create-character-button"
        >
          Create New Character
        </button>
      </div>

      {characters.length === 0 ? (
        <div className="no-characters">
          <p>You haven't created any characters yet.</p>
          <button onClick={onCreateCharacter} className="create-first-character">
            Create Your First Character
          </button>
        </div>
      ) : (
        <div className="character-grid">
          {characters.map(character => (
            <div key={character.id} className="character-card">
              <div className="character-header">
                {(character.talentPoints ?? 0) > 0 && (
                  <div className="talent-points-badge" title="Unspent Talent Points - Visit Skills!">
                    ⭐ {character.talentPoints}
                  </div>
                )}
                <div className="character-title-row">
                  <h3>{character.name}</h3>
                  <span className="level">Level {character.level}</span>
                  <span className="race-class">
                    {character.race} {character.class}
                    {character.evolvedClass && <span className="evolved-class-tag"> ⭐ {character.evolvedClass}</span>}
                  </span>
                  <span className="character-gold" title="Gold">💰 {character.gold ?? 0}</span>
                </div>
              </div>

              {/* XP Progress Bar */}
              <div className="xp-progress">
                <div className="xp-bar-container">
                  <div
                    className="xp-bar-fill"
                    style={{
                      width: `${Math.min(100, (character.experience / getTotalExperienceForLevel(character.level + 1)) * 100)}%`
                    }}
                  />
                </div>
                <span className="xp-text">{character.experience} / {getTotalExperienceForLevel(character.level + 1)} XP</span>
              </div>

              <div className="character-stats">
                <div className="primary-stats">
                  <div className="stat-cell">STR: {character.strength}</div>
                  <div className="stat-cell">DEX: {character.dexterity}</div>
                  <div className="stat-cell">VIT: {character.vitality}</div>
                  <div className="stat-cell">INT: {character.intelligence}</div>
                  <div className="stat-cell">WIS: {character.wisdom}</div>
                  <div className="stat-cell">CON: {character.constitution}</div>
                </div>
              </div>

              <div className="character-actions">
                <button
                  onClick={() => openAppearanceEditor(character)}
                  className="appearance-button"
                >
                  🎨 Appearance
                </button>
                {onViewProgression && (
                  <button
                    onClick={() => onViewProgression(character)}
                    className="progression-button"
                  >
                    Skills
                  </button>
                )}
                {onManageEquipment && (
                  <button
                    onClick={() => onManageEquipment(character)}
                    className="equipment-button"
                  >
                    Equip
                  </button>
                )}
                <button
                  onClick={() => handleDeleteCharacter(character.id, character.name)}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {appearanceCharacter && appearanceDraft && (
        <div className="modal-overlay" onClick={closeAppearanceEditor}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎨 {appearanceCharacter.name}'s Appearance</h3>
              <button onClick={closeAppearanceEditor}>×</button>
            </div>
            <div className="modal-body">
              <PlayerAppearanceEditor
                race={appearanceCharacter.race}
                value={appearanceDraft}
                onChange={setAppearanceDraft}
              />
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={closeAppearanceEditor}>Cancel</button>
              <button
                className="save-appearance-button"
                onClick={handleSaveAppearance}
                disabled={savingAppearance}
              >
                {savingAppearance ? 'Saving...' : 'Save Appearance'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterList;